export interface GradedPart {
    content: string;
    studentContent?: string;
    isCorrect?: boolean;
    score?: number;
    maxScore?: number;
    feedback?: string;
}

export interface ScanResult {
    parts: GradedPart[];
    rawText?: string;
}

export interface GradeResult {
    parts: GradedPart[];
    totalScore: number;
    totalMaxScore: number;
    overallFeedback: string;
    teacherSolution: string;
    imageUrl?: string;
}

class AutoCoringService {
    private proxyUrl: string;

    constructor() {
        this.proxyUrl = import.meta.env.VITE_OPENAI_PROXY_URL || 'https://vatli365.vn/openai-proxy.php';
    }

    private fileToBase64(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = error => reject(error);
        });
    }

    /**
     * Step 1: Scan image to extract content (OCR)
     */
    async scanSubmission(imageFile: File, questionText: string): Promise<ScanResult> {
        try {
            const base64Image = await this.fileToBase64(imageFile);

            const prompt = `
Bạn là một trợ lý AI hỗ trợ giáo viên.
Nhiệm vụ: Đọc bài làm của học sinh từ hình ảnh và PHÂN TÍCH theo cấu trúc đáp án.

ĐỀ BÀI:
${questionText}

YÊU CẦU QUAN TRỌNG:
1. **PHÂN TÍCH CẤU TRÚC ĐỀ BÀI**:
   - Đọc kỹ đề bài để xác định các "ý" cần trả lời
   - Thông thường đề bài Vật lý có 3-4 ý chính (VD: Ý 1: Viết công thức, Ý 2: Thay số, Ý 3: Tính toán, Ý 4: Kết luận)
   - Mỗi ý thường kết thúc bằng dấu chấm (.)

2. **TRÍCH XUẤT VÀ PHÂN LOẠI BÀI LÀM (NGUYÊN TẮC VÀNG: TRUNG THỰC TUYỆT ĐỐI)**:
   - **KHÔNG LÀM TRÒN SỐ**: Đây là yêu cầu quan trọng nhất. Nếu học sinh viết "0,507", bạn PHẢI viết lại là "0,507", TUYỆT ĐỐI KHÔNG được sửa thành "0,5" hay "0,51".
   - **GIỮ NGUYÊN DẤU THẬP PHÂN**: Nếu học sinh dùng dấu phẩy (,) hay dấu chấm (.), bạn phải giữ nguyên y hệt.
   - **TUYỆT ĐỐI KHÔNG SỬA LỖI**: Nếu học sinh viết sai chính tả, sai công thức, hay tính toán sai, BẠN PHẢI GIỮ NGUYÊN LỖI ĐÓ.
   - **KHÔNG TỰ ĐOÁN**: Nếu chữ quá xấu không đọc được, hãy ghi "[không rõ]". Không được tự ý điền nội dung cho hợp lý.

OUTPUT FORMAT:
\`\`\`json
            {
                "parts": [
                    {
                        "content": "Nội dung ý 1 mà học sinh viết"
                    },
                    {
                        "content": "Nội dung ý 2 mà học sinh viết"
                    },
                    {
                        "content": "Nội dung ý 3 mà học sinh viết"
                    }
                ]
            }
            `;

            const payload = {
                model: "gpt-4o",
                messages: [
                    {
                        role: "user",
                        content: [
                            { type: "text", text: prompt },
                            { type: "image_url", image_url: { url: base64Image } },
                        ],
                    },
                ],
                max_tokens: 1000,
            };

            const response = await fetch(this.proxyUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                const errorText = await response.text().catch(() => 'Unknown error');
                console.error('API Error Response:', {
                    status: response.status,
                    statusText: response.statusText,
                    body: errorText,
                    proxyUrl: this.proxyUrl
                });

                if (response.status === 502) {
                    throw new Error(`Lỗi kết nối đến AI server(502).Vui lòng kiểm tra: \n1.Server proxy có đang chạy không ?\n2.VITE_OPENAI_PROXY_URL trong.env có đúng không ?\n3.API key có hợp lệ không ? `);
                }

                throw new Error(`API Error: ${response.status} ${response.statusText} `);
            }

            const data = await response.json();
            const content = data.choices?.[0]?.message?.content;
            if (!content) throw new Error('No content received from AI');

            console.log('📄 [AutoCoringService] Raw AI response:', content);

            let cleanContent = content.replace(/```json/g, '').replace(/```/g, '').trim();

            try {
                const parsed = JSON.parse(cleanContent);
                // Handle both "parts" (requested) and "result" (sometimes returned by AI)
                const parts = parsed.parts || parsed.result || [];

                return {
                    parts: parts,
                    rawText: content
                };
            } catch (parseError) {
                console.warn('⚠️ [AutoCoringService] JSON parse failed, attempting sanitization...');

                // Sanitize: escape invalid backslashes
                cleanContent = cleanContent.replace(/\\(?!["\\/bfnrtu])/g, '\\\\');
                cleanContent = cleanContent.replace(/\\u(?![0-9a-fA-F]{4})/g, '\\\\u');

                try {
                    const parsed = JSON.parse(cleanContent);
                    const parts = parsed.parts || parsed.result || [];
                    return {
                        parts: parts,
                        rawText: content
                    };
                } catch (retryError) {
                    console.error('❌ [AutoCoringService] JSON parse failed after sanitization:', retryError);
                    // Fallback: Try to find array in text if JSON parse fails completely
                    return {
                        parts: [],
                        rawText: content
                    };
                }
            }

        } catch (error) {
            console.error('Scan failed:', error);
            throw error;
        }
    }

    /**
     * Step 2: Grade the extracted content
     */
    async gradeSubmission(parts: GradedPart[], questionText: string, answerKey: string, options?: { penalizeMissingUnit: boolean }): Promise<GradeResult> {
        try {
            // Format student work with part numbers for clarity
            const studentWorkFormatted = parts.map((p, index) =>
                `Ý ${index + 1}: ${p.content || '(Học sinh không viết)'}`
            ).join('\n');

            const penalizeUnitInstruction = options?.penalizeMissingUnit
                ? "- KIỂM TRA ĐƠN VỊ: Nếu kết quả cuối cùng thiếu hoặc sai đơn vị, hãy TRỪ 0.25 điểm."
                : "- Bỏ qua lỗi thiếu đơn vị.";

            const prompt = `
Bạn là giáo viên Vật lí. Hãy chấm điểm bài làm của học sinh theo CHÍNH XÁC thang điểm trong đáp án.

ĐỀ BÀI:
${questionText}

ĐÁP ÁN CHUẨN (kèm thang điểm chi tiết):
${answerKey}

BÀI LÀM CỦA HỌC SINH (đã phân tích thành ${parts.length} ý):
${studentWorkFormatted}

YÊU CẦU BẮT BUỘC:
1. **BƯỚC 1: SUY LUẬN (Thinking Process)**:
   - Trước khi trả về JSON, hãy suy nghĩ từng bước:
   - Liệt kê tất cả các điểm số tìm thấy trong ĐÁP ÁN CHUẨN (ví dụ: 0.25, 0.25, 0.25, 0.25).
   - Tính tổng maxScore (ví dụ: 0.25 + 0.25 + 0.25 + 0.25 = 1.0).
   - So sánh từng ý của học sinh với đáp án để quyết định điểm số.
   - Tính tổng score thực tế.

2. **BƯỚC 2: TẠO JSON (Kết quả cuối cùng)**:
   - **maxScore**: Lấy CHÍNH XÁC từ con số trong đáp án.
   - **score**:
     + Nếu đúng hoàn toàn → score = maxScore
     + Nếu sai hoặc không làm → score = 0
     + Nếu đúng một phần (ví dụ sai đơn vị, thiếu bước nhỏ) → trừ điểm theo hướng dẫn hoặc trừ 50% số điểm của ý đó.
   - ${penalizeUnitInstruction}
   - **totalScore**: Phải bằng TỔNG CỘNG (SUM) của tất cả các score. KHÔNG ĐƯỢC TÍNH TRUNG BÌNH CỘNG.

3. **ĐỊNH DẠNG FEEDBACK**:
   - Dùng LaTeX cho công thức: $F = ma$.
   - Không dùng ngoặc đơn bao quanh công thức.

OUTPUT FORMAT:
Hãy in ra phần suy luận trước, sau đó là khối JSON.

Ví dụ suy luận:
"Tìm thấy 4 điểm 0.25 trong đáp án. Tổng maxScore = 1.0. Ý 1 đúng (0.25), Ý 2 sai (0), Ý 3 đúng (0.25), Ý 4 đúng (0.25). Tổng score = 0.75."

\`\`\`json
{
  "parts": [
    {
      "content": "Nội dung ý 1 trong đáp án chuẩn",
      "studentContent": "Nội dung ý 1 mà học sinh viết",
      "isCorrect": true,
      "score": 0.25,
      "maxScore": 0.25,
      "feedback": "Học sinh làm đúng công thức $F = ma$."
    }
    // ... tiếp tục cho đến hết ${parts.length} parts
  ],
  "totalScore": 0.75,
  "totalMaxScore": 1.0,
  "overallFeedback": "Nhận xét chung về bài làm",
  "teacherSolution": "Đáp án chuẩn đầy đủ (định dạng LaTeX)"
}
\`\`\`
`;

            const payload = {
                model: "gpt-4o",
                messages: [
                    { role: "user", content: prompt }
                ],
                max_tokens: 1500,
            };

            const response = await fetch(this.proxyUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                const errorText = await response.text().catch(() => 'Unknown error');
                console.error('API Error Response:', {
                    status: response.status,
                    statusText: response.statusText,
                    body: errorText,
                    proxyUrl: this.proxyUrl
                });

                if (response.status === 502) {
                    throw new Error(`Lỗi kết nối đến AI server (502).`);
                }

                throw new Error(`API Error: ${response.status} ${response.statusText} `);
            }

            const data = await response.json();
            const content = data.choices?.[0]?.message?.content;
            if (!content) throw new Error('No content received from AI');

            let cleanContent = content.replace(/```json/g, '').replace(/```/g, '').trim();

            // Extract JSON object
            const firstOpen = cleanContent.indexOf('{');
            const lastClose = cleanContent.lastIndexOf('}');
            if (firstOpen !== -1 && lastClose !== -1) {
                cleanContent = cleanContent.substring(firstOpen, lastClose + 1);
            }

            try {
                const parsed = JSON.parse(cleanContent);
                return {
                    parts: parsed.parts || [],
                    totalScore: parsed.totalScore || 0,
                    totalMaxScore: parsed.totalMaxScore || 0,
                    overallFeedback: parsed.overallFeedback || '',
                    teacherSolution: parsed.teacherSolution || ''
                };
            } catch (parseError) {
                console.warn('⚠️ [AutoCoringService] JSON parse failed, attempting sanitization...');

                // Sanitize: escape invalid backslashes
                cleanContent = cleanContent.replace(/\\(?!["\\/bfnrtu])/g, '\\\\');
                cleanContent = cleanContent.replace(/\\u(?![0-9a-fA-F]{4})/g, '\\\\u');

                const parsed = JSON.parse(cleanContent);
                return {
                    parts: parsed.parts || [],
                    totalScore: parsed.totalScore || 0,
                    totalMaxScore: parsed.totalMaxScore || 0,
                    overallFeedback: parsed.overallFeedback || '',
                    teacherSolution: parsed.teacherSolution || ''
                };
            }

        } catch (error) {
            console.error('Grading failed:', error);
            throw error;
        }
    }

    // Compatibility wrapper
    async scanAndGrade(imageFile: File, questionText: string, answerKey: string): Promise<ScanResult & GradeResult> {
        const scanRes = await this.scanSubmission(imageFile, questionText);
        const gradeRes = await this.gradeSubmission(scanRes.parts, questionText, answerKey);
        return { ...scanRes, ...gradeRes };
    }
}

export const autoCoringService = new AutoCoringService();
